import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { GetRunsParams, GetRunsResult, getRuns } from 'utils/api'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import { CELL_VIS_MAX_SAMPLE_SIZE } from '../startAnalysis/utils'

export interface LabelingEligibility {
  /** Whether the current session can be labeled */
  canLabel: boolean
  /** Human-readable reason when canLabel is false (used for the disabled-state tooltip) */
  reason?: string
  /**
   * Set when the session is labelable but does not cover the whole run (the run
   * exceeds the Cell Vis sampling cap). Explains the partial coverage so labels
   * are not mistaken for whole-run coverage.
   */
  warning?: string
  /** True while eligibility is still being determined (e.g. fetching the run total) */
  loading: boolean
}

// A session counts as a "full run" when it holds essentially all of the run's
// cells. The small tolerance absorbs QC trimming so a genuinely full session is
// not rejected, while a clearly downsampled session stays ineligible.
const FULL_RUN_MIN_FRACTION = 0.99

export interface EligibilityInput {
  hasSession: boolean
  singleRun: boolean
  runLoading: boolean
  /** The run's total cell count, once the runs query resolves */
  runTotal?: number
  /** Requested sample size at session creation (create_spec.max_cell_count) */
  requestedCells?: number
  /** Cells actually loaded into the dataframe */
  loadedCells: number
}

/**
 * Pure eligibility decision. Labeling requires a single run used at 100% of its
 * cells, so the labeler sees every cell (no sampled cells can leak into a label).
 * `max_cell_count` (the requested sample size) is used as the "full run" signal:
 * embeddings exist for every cell (HFM runs on the controller during imaging),
 * so the only gap between loaded and total is QC trimming; the loaded count is a
 * fallback when the cap is missing.
 */
export const evaluateLabelingEligibility = (input: EligibilityInput): LabelingEligibility => {
  const { hasSession, singleRun, runLoading, runTotal, requestedCells, loadedCells } = input

  if (!hasSession) return { canLabel: false, loading: true }

  if (!singleRun) {
    return {
      canLabel: false,
      loading: false,
      reason: 'Labeling requires a session built from a single run.',
    }
  }

  if (runLoading) return { canLabel: false, loading: true }

  if (!runTotal) {
    return {
      canLabel: false,
      loading: false,
      reason: 'Labeling is unavailable until run details load.',
    }
  }

  // Still determining: no cap yet and cells not loaded
  if (requestedCells === undefined && loadedCells === 0) {
    return { canLabel: false, loading: true }
  }

  const coversFullRun =
    (requestedCells !== undefined && requestedCells >= runTotal) ||
    loadedCells >= runTotal * FULL_RUN_MIN_FRACTION

  if (coversFullRun) return { canLabel: true, loading: false }

  // The session is partial. Allow it only when the gap is the Cell Vis sampling
  // cap (the run simply has more cells than the platform can sample), not a
  // deliberately smaller sample. A capped session is the most complete coverage
  // the platform allows, so it is labelable but carries a coverage warning.
  const atPlatformCap =
    requestedCells !== undefined
      ? requestedCells >= CELL_VIS_MAX_SAMPLE_SIZE
      : loadedCells >= CELL_VIS_MAX_SAMPLE_SIZE * FULL_RUN_MIN_FRACTION

  if (atPlatformCap && runTotal > CELL_VIS_MAX_SAMPLE_SIZE) {
    const usedCells = loadedCells || requestedCells || CELL_VIS_MAX_SAMPLE_SIZE
    return {
      canLabel: true,
      loading: false,
      warning: `This run has ${runTotal.toLocaleString()} cells, more than the ${CELL_VIS_MAX_SAMPLE_SIZE.toLocaleString()}-cell Cell Vis limit. Labeling covers the ${usedCells.toLocaleString()} cells in this session; the remaining cells are not included.`,
    }
  }

  return {
    canLabel: false,
    loading: false,
    reason: 'Labeling requires a session that used 100% of the run cells (this session is downsampled).',
  }
}

/**
 * Determines whether the currently loaded Cell Vis session is eligible for the
 * internal labeling workflow.
 */
export const useLabelingEligibility = (): LabelingEligibility => {
  const {
    dataFrame,
    cellVisualizations: { sessionData },
  } = useCellVisualizationsSlice()

  const runIds = useMemo(
    () =>
      (sessionData?.runs ?? [])
        .map((run) => run.run_id)
        .filter((id): id is string => !!id && id.trim() !== ''),
    [sessionData]
  )
  const singleRun = runIds.length === 1
  const runId = singleRun ? runIds[0] : undefined

  const params: GetRunsParams = { run_ids: runId ? [runId] : [] }
  const { data: runs, isLoading: runLoading } = useQuery<
    GetRunsResult,
    Error,
    GetRunsResult,
    [string, GetRunsParams]
  >(['labelingRunTotal', params], getRuns, { enabled: !!runId, staleTime: Infinity })

  return useMemo<LabelingEligibility>(
    () =>
      evaluateLabelingEligibility({
        hasSession: Boolean(sessionData),
        singleRun,
        runLoading,
        runTotal: runs?.[0]?.total_cell_count,
        requestedCells: sessionData?.create_spec?.max_cell_count,
        loadedCells: dataFrame?.numRows() ?? 0,
      }),
    [sessionData, singleRun, runLoading, runs, dataFrame]
  )
}

export default useLabelingEligibility
